/**
 * Application Constants
 * Centralized configuration for the Voyager application
 */

// Breakpoints for responsive design
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
} as const;

// Animation configurations
export const ANIMATIONS = {
  DURATION: {
    FAST: 200,
    NORMAL: 400,
    SLOW: 600,
    FADE_IN: 600,
    SLIDE_UP: 800,
    SCALE_IN: 500,
  },
  EASING: {
    SMOOTH: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    EASE_OUT: 'ease-out',
    EASE_IN_OUT: 'ease-in-out',
  },
  DELAYS: {
    STAGGER_BASE: 0.1, // seconds for staggered animations
  },
} as const;

// Audio player configurations
export const AUDIO = {
  SKIP_BACK_SECONDS: 15,
  SKIP_FORWARD_SECONDS: 15,
  PRELOAD_MODE: 'metadata' as const,
  PLAYBACK_RATES: [0.75, 1, 1.25, 1.5, 1.75, 2] as const,
  DEFAULT_PLAYBACK_RATE: 1,
  SUPPORTED_FORMATS: ['.mp3', '.wav', '.ogg'] as const,
  SEEK_TIMEOUT: 100, // ms delay after seeking to prevent race conditions
} as const;

// Book and chapter configurations
export const BOOKS = {
  MAX_HISTORY_ENTRIES: 15,
  SUPPORTED_BOOK_DIRECTORIES: ['memory-thief', 'midnight-choice', 'space-rebellion'] as const,
  DEFAULT_CHAPTER_FILE: '01.md',
  PATHS: {
    CONTENT_BASE: '/content/books',
    AUDIO_SUBDIR: 'audio',
    IMAGES_SUBDIR: 'images',
    STORY_SUBDIR: 'story',
  },
} as const;

// UI and styling constants
export const UI = {
  BORDER_RADIUS: {
    SMALL: 4,
    MEDIUM: 8,
    LARGE: 16,
    EXTRA_LARGE: 20,
  },
  GLASSMORPHISM: {
    BACKGROUND: 'linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
    BACKDROP_FILTER: 'blur(20px) saturate(180%)',
    BORDER: '1px solid rgba(255, 255, 255, 0.1)',
    BOX_SHADOW: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  Z_INDEX: {
    MODAL: 1000,
    DROPDOWN: 100,
    HEADER: 10,
    DEFAULT: 1,
  },
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  ADVANCED_CONTROLS: 'voyager-show-advanced-controls',
  READING_PROGRESS: 'voyager-reading-progress',
  USER_PREFERENCES: 'voyager-user-preferences',
} as const;

// Error messages
export const ERROR_MESSAGES = {
  BOOK_NOT_FOUND: 'Book not found',
  FAILED_TO_LOAD_BOOK: 'Failed to load book',
  FAILED_TO_LOAD_AUDIO: 'Failed to load audio',
  FAILED_TO_PLAY_AUDIO: 'Failed to play audio',
  NETWORK_ERROR: 'Network error occurred',
  GENERIC_ERROR: 'An unexpected error occurred',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  FEEDBACK_SENT: 'Feedback sent successfully',
  PROGRESS_SAVED: 'Progress saved',
} as const;

// API and service configurations
export const API = {
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;